import { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { api } from '../lib/api';

export function AccountDeletePage() {
  const { user, refreshAuth } = useAuth();
  const toast = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [saving, setSaving] = useState(false);

  const deleteDateLabel = useMemo(() => {
    if (!user?.deleteRequestedAt) return null;
    return new Date(user.deleteRequestedAt).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
  }, [user?.deleteRequestedAt]);

  const requestDelete = async () => {
    if (confirmText.trim() !== 'DELETE') {
      toast.error('Skriv DELETE för att bekräfta');
      return;
    }
    setSaving(true);
    try {
      await api.post<{ ok: boolean; deleteRequestedAt: string }>('/auth/request-account-delete', { confirmText: 'DELETE' });
      await refreshAuth();
      setConfirmText('');
      toast.info('Kontoborttagning begärd. Kontot raderas om 14 dagar.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Kunde inte begära kontoborttagning');
    } finally {
      setSaving(false);
    }
  };

  const cancelDelete = async () => {
    setSaving(true);
    try {
      await api.post<{ ok: boolean }>('/auth/cancel-account-delete');
      await refreshAuth();
      toast.success('Kontoborttagning avbruten');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Kunde inte avbryta kontoborttagning');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <h1 className="text-lg font-semibold">Ta bort konto</h1>
      <div className="border border-gray-200 rounded p-3 bg-gray-50 space-y-3">
        <p className="text-sm text-gray-700">
          När du begär kontoborttagning raderas kontot efter 14 dagar om du inte avbryter innan dess.
        </p>
        {deleteDateLabel ? (
          <>
            <p className="text-sm text-gray-700">
              Kontot är markerat för borttagning: <span className="font-medium">{deleteDateLabel}</span>
            </p>
            <button
              type="button"
              onClick={cancelDelete}
              disabled={saving}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              Avbryt kontoborttagning
            </button>
          </>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700">
              Skriv <code>DELETE</code> för att bekräfta
            </label>
            <input
              type="text"
              className="w-full"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
            />
            <button
              type="button"
              onClick={requestDelete}
              disabled={saving}
              className="px-2 py-1 text-sm bg-danger text-white rounded hover:bg-danger-hover disabled:opacity-50"
            >
              Begär kontoborttagning
            </button>
          </>
        )}
      </div>
    </div>
  );
}

